import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Eye, EyeOff } from 'lucide-react';

const STORAGE_PREFIX = 'bondMentorCollapsibleSection_v42c_';
const SETTINGS_KEY = 'bondMentorUserSettings_v43e2';

/**
 * Read the user's global Mission Control layout preference:
 *   'expanded'  — every section opens by default
 *   'collapsed' — every section starts closed
 *   'remember'  — use each section's last saved open/closed choice
 */
function readLayoutMode() {
  if (typeof window === 'undefined') return 'expanded';
  try {
    const raw = window.localStorage.getItem(SETTINGS_KEY);
    const mode = raw ? JSON.parse(raw)?.missionControlLayout : null;
    if (mode === 'collapsed' || mode === 'remember' || mode === 'expanded') return mode;
  } catch {
    // Fall through to the default if settings can't be read.
  }
  return 'expanded';
}

function readStoredState(storageKey, defaultOpen) {
  if (!storageKey || typeof window === 'undefined') return defaultOpen;
  try {
    const saved = window.localStorage.getItem(`${STORAGE_PREFIX}${storageKey}`);
    if (saved === 'open') return true;
    if (saved === 'closed') return false;
  } catch {
    // Local storage is helpful memory only. If it fails, use the default.
  }
  return defaultOpen;
}

function resolveOpen(mode, storageKey, defaultOpen) {
  if (mode === 'expanded') return true;
  if (mode === 'collapsed') return false;
  return readStoredState(storageKey, defaultOpen);
}

export default function CollapsibleSection({
  id,
  title,
  eyebrow = 'Advanced context',
  summary,
  badge,
  defaultOpen = true,
  children,
}) {
  const storageKey = useMemo(() => id || title?.toLowerCase().replace(/[^a-z0-9]+/g, '-'), [id, title]);
  const [layoutMode, setLayoutMode] = useState(() => readLayoutMode());
  const [isOpen, setIsOpen] = useState(() => resolveOpen(readLayoutMode(), storageKey, defaultOpen));

  // React live when the user changes the Mission Control layout preference,
  // or when another browser tab updates it.
  useEffect(() => {
    const applyMode = () => {
      const mode = readLayoutMode();
      setLayoutMode(mode);
      setIsOpen(resolveOpen(mode, storageKey, defaultOpen));
    };
    window.addEventListener('bondmentor:settings', applyMode);
    window.addEventListener('storage', applyMode);
    return () => {
      window.removeEventListener('bondmentor:settings', applyMode);
      window.removeEventListener('storage', applyMode);
    };
  }, [storageKey, defaultOpen]);

  const handleToggle = useCallback(() => {
    setIsOpen((value) => {
      const next = !value;
      // Only persist per-section choices in "remember" mode, so the user's
      // saved layout isn't overwritten while they're in expanded/collapsed mode.
      if (layoutMode === 'remember') {
        try {
          window.localStorage.setItem(`${STORAGE_PREFIX}${storageKey}`, next ? 'open' : 'closed');
        } catch {
          // Do not block the dashboard if localStorage is unavailable.
        }
      }
      return next;
    });
  }, [layoutMode, storageKey]);

  return (
    <section className="glass-card border border-mentor-border/55 overflow-hidden animate-slide-up">
      <button
        type="button"
        onClick={handleToggle}
        className="w-full text-left p-4 sm:p-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between hover:bg-white/[0.02] transition-colors"
        aria-expanded={isOpen}
        aria-controls={`${storageKey}-content`}
      >
        <div className="flex items-start gap-3 min-w-0">
          <div className="mt-0.5 w-9 h-9 rounded-xl border border-mentor-accent/25 bg-mentor-accent/10 flex items-center justify-center text-mentor-accent shrink-0">
            {isOpen ? <Eye size={17} /> : <EyeOff size={17} />}
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <p className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-mentor-muted">
                {eyebrow}
              </p>
              {badge && (
                <span className="px-2 py-0.5 rounded-full border border-mentor-accent/25 bg-mentor-accent/10 text-[10px] font-mono font-bold text-mentor-accent uppercase">
                  {badge}
                </span>
              )}
            </div>
            <h3 className="font-display font-bold text-base sm:text-lg text-mentor-text">
              {title}
            </h3>
            {summary && (
              <p className="text-xs sm:text-sm text-mentor-muted font-body leading-relaxed mt-1 max-w-4xl">
                {summary}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 self-start lg:self-center shrink-0">
          <span className="text-[10px] uppercase tracking-widest text-mentor-muted font-display font-bold">
            {isOpen ? 'Hide details' : 'Show details'}
          </span>
          <span className="w-8 h-8 rounded-lg border border-mentor-border/50 bg-mentor-bg/40 flex items-center justify-center text-mentor-muted">
            {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
        </div>
      </button>

      {isOpen && (
        <div id={`${storageKey}-content`} className="border-t border-mentor-border/45 p-4 sm:p-5">
          {children}
        </div>
      )}
    </section>
  );
}
